import { useState, useEffect, useMemo, useCallback } from 'react';

// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

// Local imports
import CurrencyFormulaTextField from '../components/CurrencyFormulaTextField.js';
import { getProductName, findMatchingProducts } from '../utils/product.js';
import { FieldTypes, buyingRateFieldTypes } from '../../FieldTypes.js';
import { getMultiplier, calcWeight, calcChargeableWeight, calcWeightOrMeasurment } from '../../CalcWeight.js';

function AddCostsManuallyModal({ open, onCancel, onDone, suppliers, products, organization, transportationMethod, existingBuyingRates, editRate, routes }) {
    const defaultCurrency = organization?.defaults?.currency || 'USD';

    const [rows, setRows] = useState([]);
    const [supplierName, setSupplierName] = useState('');
    const [supplierUniqueId, setSupplierUniqueId] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [amountErrors, setAmountErrors] = useState(new Set());

    // Reset state each time modal opens — pre-fill from editRate if editing
    useEffect(() => {
        if (open) {
            setAmountErrors(new Set());
            if (editRate) {
                setSupplierName(editRate.supplier?.organization || '');
                const editUid = editRate.supplier?.uniqueId;
                setSupplierUniqueId(editUid && editUid.replace(/:/g, '').trim() ? editUid : null);
                setSelectedProductId(editRate.product?.id || products?.[0]?.id || null);

                const fields = _.flatten((editRate.offer?.sections || []).map(s => s.offers?.[0]?.fields || []));
                const editRows = fields.map(field => {
                    const fieldValueId = field.type === 'flat' ? 'flat' : editRate.product?.id;
                    const val = field.values?.[fieldValueId] || field.values?.flat || {};
                    return {
                        id: field.id,
                        title: field.title || '',
                        value: val.formula || (val.value ?? 0),
                        currency: val.currency || defaultCurrency,
                        type: field.type || 'per-unit-type',
                    };
                });
                setRows(editRows.length > 0 ? editRows : [createEmptyRow()]);
            } else {
                setSupplierName('');
                setSupplierUniqueId(null);
                setSelectedProductId(products?.[0]?.id || null);
                setRows([createEmptyRow()]);
            }
        }
    }, [open]);

    function createEmptyRow() {
        return {
            id: uuidv4(),
            title: '',
            value: 0,
            currency: defaultCurrency,
            type: transportationMethod === 'air' ? 'per-unit-weight' : 'per-unit-type',
        };
    }

    const supplierOptions = useMemo(() => {
        const selectedRoute = routes?.[0] || null;
        const activeSuppliers = _(suppliers).filter(s => s && s.name && !s.inactive).value();

        return _(activeSuppliers).map(s => {
            const supplierRoutes = s.transportationMethods?.[transportationMethod]?.routes || [];
            let group = 'Other suppliers';
            if (selectedRoute) {
                if (supplierRoutes.find(r => r.source?.countryCode === selectedRoute.source?.countryCode && r.destination?.countryCode === selectedRoute.destination?.countryCode)) {
                    group = 'Routes found';
                } else if (supplierRoutes.find(r => r.source?.countryCode === selectedRoute.source?.countryCode || r.destination?.countryCode === selectedRoute.destination?.countryCode)) {
                    group = 'Partial route match';
                }
            }
            return { name: s.name, uniqueId: s.uniqueId, group };
        })
        .uniqBy(o => (o.uniqueId && o.uniqueId.replace(/:/g, '').trim()) ? o.uniqueId : o.name.toLowerCase())
        .sortBy(o => (o.group === 'Routes found' ? 0 : o.group === 'Partial route match' ? 1 : 2) + '_' + o.name.toUpperCase()).value();
    }, [suppliers, routes, transportationMethod]);

    const supplierFilterOptions = (options, { inputValue }) => {
        const q = (inputValue || '').trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => (typeof o === 'string' ? o : o?.name || '').toLowerCase().includes(q));
    };

    // Per row, so a cost that is already a type this mode does not otherwise offer still has an
    // option to sit on instead of leaving the dropdown blank.
    const fieldTypeOptionsFor = useCallback(
        currentType => buyingRateFieldTypes({ transportationMethod, organization, currentType }),
        [transportationMethod, organization],
    );

    function updateRow(id, field, value) {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        if (field === 'value') {
            setAmountErrors(prev => { const next = new Set(prev); next.delete(id); return next; });
        }
    }

    function handleSupplierChange(newSupplierName) {
        // Match from deduped options first, then fall back to raw suppliers
        const opt = supplierOptions.find(o => o.name.toUpperCase() === newSupplierName?.toUpperCase());
        const sup = opt || suppliers.find(s => s.name.toUpperCase() === newSupplierName?.toUpperCase());
        setSupplierName(sup ? sup.name : newSupplierName || '');
        const uid = sup?.uniqueId;
        setSupplierUniqueId(uid && uid.replace(/:/g, '').trim() ? uid : null);
    }

    function deleteRow(id) {
        setRows(prev => {
            const filtered = prev.filter(r => r.id !== id);
            return filtered.length === 0 ? [createEmptyRow()] : filtered;
        });
    }

    function addRow() {
        setRows(prev => [...prev, createEmptyRow()]);
    }

    function buildFields(rowGroup, productId) {
        return rowGroup.map(row => {
            const fieldValues = {};
            const numValue = parseFloat(row.value);
            const isFormula = row.value && isNaN(numValue);
            const entry = isFormula
                ? { formula: row.value, currency: row.currency }
                : { value: numValue || 0, currency: row.currency };

            if (row.type === 'flat') {
                fieldValues.flat = entry;
            } else {
                fieldValues.flat = entry;
                fieldValues[productId] = entry;
            }
            return {
                id: row.id || uuidv4(),
                title: row.title,
                type: row.type,
                values: fieldValues,
            };
        });
    }

    function onOk() {
        // Validate amounts — all rows must have a non-zero amount or a formula
        const rowsWithBadAmount = rows.filter(r => {
            if (r.value && isNaN(parseFloat(r.value))) return false; // formula string — valid
            const val = parseFloat(r.value);
            return isNaN(val) || val === 0;
        });
        if (rowsWithBadAmount.length > 0) {
            setAmountErrors(new Set(rowsWithBadAmount.map(r => r.id)));
            return;
        }

        // Auto-generate default descriptions for rows with empty titles
        let chargeCounter = 0;
        const rowsWithTitles = rows.map(r => {
            if (!r.title || !r.title.trim()) {
                chargeCounter++;
                return { ...r, title: `Charge ${chargeCounter}` };
            }
            return r;
        });

        // All rows use the top-level product
        const validRows = selectedProductId ? rowsWithTitles : [];

        // When editing and no valid rows remain, signal deletion
        if (editRate && validRows.length === 0) {
            onDone({ newRates: [{ _deleteRate: editRate.id }] });
            return;
        }

        if (validRows.length === 0) {
            onCancel();
            return;
        }

        const newRates = [];

        // Check if supplier changed during edit
        const supplierChanged = editRate && (
            supplierName !== (editRate.supplier?.organization || '') ||
            supplierUniqueId !== (editRate.supplier?.uniqueId || null)
        );

        if (editRate) {
            const productId = editRate.product?.id || selectedProductId;
            const fields = buildFields(validRows, productId);
            const rateUpdate = { _replaceIn: editRate.id, _newFields: fields };
            if (supplierChanged) {
                rateUpdate._supplierUpdate = { organization: supplierName, uniqueId: supplierUniqueId };
            }
            newRates.push(rateUpdate);
        } else {
            const sup = (supplierUniqueId && suppliers.find(s => s.uniqueId === supplierUniqueId))
                || suppliers.find(s => s.name.toUpperCase() === supplierName.toUpperCase());
            if (!sup) {
                onCancel();
                return;
            }

            const product = products.find(p => p.id === selectedProductId);
            if (!product) return;

            {
                const productId = selectedProductId;
                const fields = buildFields(validRows, productId);

                // Check if existing manual rate exists for same supplier+product
                const existingManualRate = (existingBuyingRates || []).find(br =>
                    isManualBuyingRate(br) &&
                    (supplierUniqueId
                        ? br.supplier?.uniqueId === supplierUniqueId
                        : br.supplier?.organization?.toUpperCase() === sup.name.toUpperCase()) &&
                    !_.isEmpty(findMatchingProducts({ product: br.product, products: [product] }))
                );

                if (existingManualRate) {
                    newRates.push({ _mergeInto: existingManualRate.id, _newFields: fields });
                } else {
                    const now = Date.now();
                    newRates.push({
                        id: uuidv4(),
                        platform: -2,
                        created: now,
                        modified: now,
                        supplier: { organization: sup.name, uniqueId: sup.uniqueId },
                        product: _.pick(product, ['id', 'type', 'quantity', 'dangerous', 'weight', 'volume', 'kgOrLbs', 'cmOrInches', 'width', 'height', 'length', 'chargeableWeight', 'celsiusOrFahrenheit', 'temperature', 'commodity']),
                        offer: {
                            sections: [{
                                offers: [{
                                    fields
                                }]
                            }]
                        }
                    });
                }
            }
        }

        onDone({ newRates });
    }

    const isSupplierSelected = !!supplierName.trim();

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth='lg' sx={{ zIndex: 1000005 }}>
            <DialogContent>
                <Box sx={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px', fontFamily: 'Poppins, sans-serif' }}>
                    {editRate ? 'Edit Costs' : 'Add Costs Manually'}
                </Box>

                {/* Top-level supplier and product selection */}
                <Box sx={{ marginBottom: '20px', display: 'flex', gap: 2 }}>
                    <Autocomplete
                        value={supplierName || ''}
                        onChange={(e, newValue) => {
                            if (newValue && typeof newValue === 'object' && newValue.name) {
                                // Selected from dropdown — use exact option
                                setSupplierName(newValue.name);
                                const uid = newValue.uniqueId;
                                setSupplierUniqueId(uid && uid.replace(/:/g, '').trim() ? uid : null);
                            } else {
                                handleSupplierChange(typeof newValue === 'string' ? newValue : newValue?.name || newValue);
                            }
                        }}
                        options={supplierOptions}
                        filterOptions={supplierFilterOptions}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                        groupBy={(option) => option.group || 'Other suppliers'}
                        isOptionEqualToValue={(option, value) => (option.name || option) === value}
                        renderOption={(props, option) => (
                            <li {...props} key={option?.uniqueId || option?.name}>{option?.name}</li>
                        )}
                        freeSolo
                        disabled={!!editRate}
                        renderInput={(params) => <TextField {...params} variant='outlined' size='small' label='Supplier' placeholder='Select supplier' />}
                        slotProps={{
                            popper: { style: { zIndex: 1000020 } }
                        }}
                        size='small'
                        sx={{ flex: 1 }}
                    />
                    <Select
                        value={selectedProductId || ''}
                        onChange={e => setSelectedProductId(e.target.value)}
                        size='small'
                        disabled={!!editRate}
                        displayEmpty
                        sx={{ minWidth: 200 }}
                        MenuProps={{ sx: { zIndex: 1000020 } }}
                    >
                        <MenuItem value='' disabled>Select product</MenuItem>
                        {(products || []).map(p => {
                            let label = getProductName({ product: p });
                            if (p.type === 'Air freight' || p.type === 'Air Freight') {
                                const cw = calcChargeableWeight({ product: p, transportationMethod: 'air', allProducts: products, round: organization?.airRoundChargeableWeight });
                                const gw = calcWeight({ product: p });
                                if (cw?.val) label += `, ${cw.val.toLocaleString()} ${cw.postfix} chargeable weight`;
                                if (gw?.val) label += `, ${gw.val.toLocaleString()} ${gw.postfix} gross weight`;
                            }
                            if (p.type === 'LCL' || (p.type || '').startsWith('By Volume or Weight') || (p.type || '').startsWith('By Dimensions or Weight')) {
                                const wm = calcWeightOrMeasurment({ product: p, transportationMethod: 'sea', allProducts: products });
                                if (wm?.val) label += `, W/M = ${wm.val.toLocaleString()} ${wm.postfix}`;
                            }
                            return <MenuItem key={p.id} value={p.id}>{label}</MenuItem>;
                        })}
                    </Select>
                </Box>

                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 140 }}>Description</TableCell>
                                <TableCell sx={{ minWidth: 140 }}>Amount <span style={{ color: '#ef4444' }}>*</span></TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Priced per</TableCell>
                                <TableCell sx={{ width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <TextField
                                            fullWidth
                                            size='small'
                                            value={row.title}
                                            onChange={e => updateRow(row.id, 'title', e.target.value)}
                                            placeholder='Cost description'
                                        />
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Box sx={amountErrors.has(row.id) ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#ef4444' }, '&:hover fieldset': { borderColor: '#ef4444' } } } : {}}>
                                            {(() => {
                                                const isFormula = row.value && isNaN(parseFloat(row.value));
                                                const selectedProduct = products?.find(p => p.id === selectedProductId);
                                                const mText = (() => {
                                                    if (row.type === 'flat') return 'shipment';
                                                    if (!selectedProduct) return 'unit';
                                                    try {
                                                        return getMultiplier({ product: selectedProduct, transportationMethod, allProducts: products || [] }).multiplierText || 'unit';
                                                    } catch { return 'unit'; }
                                                })();
                                                return <CurrencyFormulaTextField
                                                    organization={organization}
                                                    width='120px'
                                                    value={isFormula ? '' : row.value}
                                                    formula={isFormula ? row.value : null}
                                                    currency={row.currency}
                                                    setValue={v => updateRow(row.id, 'value', v)}
                                                    setCurrency={v => updateRow(row.id, 'currency', v)}
                                                    setFormula={v => updateRow(row.id, 'value', !v ? 0 : typeof v === 'string' ? v : v.formula || v)}
                                                    multiplierText={mText}
                                                    onCancelBlur={() => {}}
                                                    showLCLWM={row.type === 'flat' ? products.some(p => p.type === 'LCL') : selectedProduct?.type === 'LCL'}
                                                />;
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Select
                                            value={row.type}
                                            onChange={e => updateRow(row.id, 'type', e.target.value)}
                                            size='small'
                                            MenuProps={{ sx: { zIndex: 1000000000 } }}
                                        >
                                            {fieldTypeOptionsFor(row.type).map(ft => (
                                                <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <IconButton size='small' onClick={() => deleteRow(row.id)}>
                                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Button
                    variant='text'
                    color='primary'
                    sx={{ marginTop: '12px' }}
                    onClick={addRow}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                >
                    Add Row
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant='contained' onClick={onOk} disabled={!isSupplierSelected || !selectedProductId}>OK</Button>
            </DialogActions>
        </Dialog>
    );
}

function isManualBuyingRate(br) {
    return br.platform === -2 || br.platform === undefined || br.platform === null;
}

export default AddCostsManuallyModal;
